import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSignUp } from '@clerk/clerk-react';
import Brand from '../components/shared/Brand';
import { isClerkConfigured } from '../lib/clerk-config';

type Step = 'form' | 'verify';

export default function RegisterPage() {
  // useSignUp() throws outside <ClerkProvider>, which is skipped when the
  // publishable key is missing/placeholder — explain instead of crashing.
  if (!isClerkConfigured) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md rounded-xl bg-slate-900/60 border border-slate-800 p-6 text-center">
          <h1 className="text-lg font-bold">Registration not configured</h1>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Set <code className="text-cyan-300">REACT_APP_CLERK_PUBLISHABLE_KEY</code> in{' '}
            <code className="text-cyan-300">frontend/.env.local</code> and restart the dev server.
          </p>
        </div>
      </div>
    );
  }
  return <RegisterPageInner />;
}

function RegisterPageInner() {
  const { type = 'viewer' } = useParams<{ type: string }>();
  const isInspector = type === 'inspector';
  const navigate = useNavigate();
  const { isLoaded, signUp, setActive } = useSignUp();

  // Form state
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [fullName,     setFullName]     = useState('');
  const [organisation, setOrganisation] = useState('');
  const [licenseNo,    setLicenseNo]    = useState('');

  // Verification state
  const [step,  setStep]  = useState<Step>('form');
  const [code,  setCode]  = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy,  setBusy]  = useState(false);

  /* ── Step 1: create the Clerk account ──────────────────────────────────── */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    setError(null);
    setBusy(true);

    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName: fullName.split(' ')[0] ?? fullName,
        lastName:  fullName.split(' ').slice(1).join(' ') || undefined,
        unsafeMetadata: {
          registration_type: type,
          organisation:      organisation || undefined,
          license_no:        isInspector ? licenseNo : undefined,
        },
      });

      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setStep('verify');
    } catch (err: unknown) {
      const msg = (err as { errors?: { message: string }[] })?.errors?.[0]?.message
        ?? (err instanceof Error ? err.message : 'Registration failed');
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  /* ── Step 2: verify email code ─────────────────────────────────────────── */
  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signUp || !setActive) return;
    setError(null);
    setBusy(true);

    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/');
      } else {
        setError('Verification incomplete. Please try again.');
      }
    } catch (err: unknown) {
      const msg = (err as { errors?: { message: string }[] })?.errors?.[0]?.message
        ?? (err instanceof Error ? err.message : 'Verification failed');
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  const roleLabel = isInspector ? 'Ship Inspector' : 'Viewer';
  const roleAccent = isInspector
    ? 'text-orange-300 bg-orange-900/40 border-orange-700/60'
    : 'text-cyan-300 bg-cyan-900/40 border-cyan-700/60';

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background mesh */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
        {/* Brand */}
        <div className="mb-6">
          <Brand size="md" showTagline />
        </div>

        {/* Role badge */}
        <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border mb-6 ${roleAccent}`}>
          {isInspector ? '⚓' : '◐'} Registering as {roleLabel}
        </span>

        {/* Card */}
        <div className="w-full bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-6 shadow-2xl">
          {step === 'form' ? (
            <>
              <h2 className="text-base font-semibold text-white mb-1">Create your account</h2>
              <p className="text-slate-500 text-xs mb-5">It takes about 30 seconds.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Field label="Full name"    value={fullName}     onChange={setFullName}     placeholder="Jane Smith" required />
                <Field label="Email"        value={email}        onChange={setEmail}        placeholder="jane@example.com" type="email" required />
                <Field label="Password"     value={password}     onChange={setPassword}     placeholder="8+ characters" type="password" required />
                <Field label="Organisation" value={organisation} onChange={setOrganisation} placeholder="(optional)" />

                {isInspector && (
                  <Field
                    label="Inspector licence no."
                    value={licenseNo}
                    onChange={setLicenseNo}
                    placeholder="e.g. MCA-12345"
                    required
                  />
                )}

                {error && (
                  <div className="text-red-300 text-xs bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm transition-all"
                >
                  {busy ? 'Creating account…' : 'Create account'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-base font-semibold text-white mb-1">Check your email</h2>
              <p className="text-slate-500 text-xs mb-5">
                We sent a 6-digit code to <span className="text-slate-300 font-medium">{email}</span>.
              </p>

              <form onSubmit={handleVerify} className="space-y-4">
                <Field
                  label="Verification code"
                  value={code}
                  onChange={setCode}
                  placeholder="123456"
                  maxLength={6}
                  required
                />

                {error && (
                  <div className="text-red-300 text-xs bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm transition-all"
                >
                  {busy ? 'Verifying…' : 'Verify & sign in'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('form')}
                  className="w-full text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                  ← Back to sign-up
                </button>
              </form>
            </>
          )}

          <div className="mt-5 pt-5 border-t border-slate-800 text-center">
            <span className="text-slate-500 text-xs">Already have an account? </span>
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 text-xs font-medium">
              Sign in
            </Link>
          </div>
        </div>

        {/* Toggle inspector / viewer */}
        <p className="mt-4 text-slate-500 text-xs">
          {isInspector ? (
            <>Not an inspector? <Link to="/register/viewer" className="text-cyan-400 hover:text-cyan-300">Sign up as a viewer instead →</Link></>
          ) : (
            <>Are you a ship inspector? <Link to="/register/inspector" className="text-orange-400 hover:text-orange-300">Inspector registration →</Link></>
          )}
        </p>
      </div>
    </div>
  );
}

/* ── Reusable form field ──────────────────────────────────────────────────── */
function Field({
  label, value, onChange, type = 'text', placeholder, required = false, maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/40 transition-colors"
      />
    </div>
  );
}
